import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { URGENCY_JOBS_COUNT, ACTIVE_WORKERS, AVG_MATCH_TIME, CITIES_COVERED } from "../../../mocks/jobs";

const QUICK_TAGS = ["Landscaping", "Moving & Labor", "Cleaning", "Construction", "Events", "Driving"];

const AVATARS = [
  "https://public.readdy.ai/ai/img_res/a1cc2a55254fe87d9a85b86befdc1e4e.jpg",
  "https://readdy.ai/api/search-image?query=black%20woman%20worker%20smiling%20headshot%20portrait%20plain%20light%20background&width=40&height=40&seq=av2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=asian%20male%20construction%20worker%20smiling%20headshot%20plain%20background&width=40&height=40&seq=av3&orientation=squarish",
];

export default function HeroSection() {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const stats = [
    { value: `${URGENCY_JOBS_COUNT}+`, label: "Jobs Today", icon: "ri-briefcase-4-line" },
    { value: `${(ACTIVE_WORKERS / 1000).toFixed(1)}K`, label: "Active Workers", icon: "ri-group-line" },
    { value: AVG_MATCH_TIME, label: "Avg. Match", icon: "ri-timer-flash-line" },
    { value: `${CITIES_COVERED}+`, label: "Cities", icon: "ri-map-pin-2-line" },
  ];

  return (
    <section id="home" className="relative bg-[#f3f8f5] overflow-hidden flex flex-col min-h-screen">

      {/* Decorative background shapes */}
      <div className="pointer-events-none absolute top-0 right-0 w-[720px] h-[720px] rounded-full bg-emerald-100/60 translate-x-1/3 -translate-y-1/4" />
      <div className="pointer-events-none absolute bottom-20 left-0 w-[500px] h-[500px] rounded-full bg-emerald-50/80 -translate-x-1/2 translate-y-1/4" />

      {/* ── MOBILE: Full-width logo banner ── */}
      <div className="md:hidden relative z-10 pt-24 pb-4 flex flex-col items-center bg-gradient-to-b from-emerald-50/80 to-transparent px-4">
        <Link to="/">
          <img
            src="https://static.readdy.ai/image/8b9b321cd7d670b6a60b0d1b6baf2111/e3a635c31b7543fa0ee8e8d84db9d640.png"
            alt="Gig n' Cash"
            className="h-[324px] w-auto object-contain"
          />
        </Link>
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3.5 py-1.5 rounded-full mt-3">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          {URGENCY_JOBS_COUNT}+ Jobs Available Right Now
        </div>
      </div>

      {/* ── MOBILE: Hero worker image (full-width) ── */}
      <div className="hidden relative z-10 w-full overflow-hidden flex justify-center items-end bg-gradient-to-b from-transparent via-emerald-50/30 to-[#f3f8f5]" style={{ height: 280 }}>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-emerald-100/60" />
        <img
          src="https://storage.readdy-site.link/project_files/a975f4d2-df36-474b-ab5c-75267cc8e7a8/75f2f10d-e3a5-4c9e-9c10-66a3caa639b1_ChatGPT-Image-Mar-30-2026-09_16_36-PM.png?v=f5edd74b05aa2c72ebce99ad48465108"
          alt="Gig worker"
          className="relative z-10 h-[260px] w-auto object-contain object-bottom"
        />
        {/* Floating hired badge */}
        <div className="absolute top-6 right-4 bg-white border border-gray-100 rounded-2xl px-3 py-2 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-emerald-100 flex-shrink-0">
            <img
              src="https://readdy.ai/api/search-image?query=smiling%20young%20woman%20worker%20headshot%20portrait%20plain%20neutral%20background%20professional&width=32&height=32&seq=av4&orientation=squarish"
              alt="Worker"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 whitespace-nowrap">Maria G. — Hired!</div>
            <div className="text-[10px] text-emerald-500 font-semibold">2 min ago</div>
          </div>
        </div>
        {/* Floating jobs badge */}
        <div className="absolute top-6 left-4 bg-emerald-500 rounded-xl px-3 py-2">
          <div className="text-lg font-extrabold text-white leading-tight">{URGENCY_JOBS_COUNT}+</div>
          <div className="text-[10px] text-emerald-100 font-semibold">Open Jobs</div>
        </div>
      </div>

      {/* ── Main two-column grid ── */}
      <div className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-5 md:px-12 lg:px-16 pt-4 md:pt-20 pb-12 grid md:grid-cols-2 gap-8 lg:gap-12 items-center">

        {/* ─── LEFT: Text content ─── */}
        <div className={`flex flex-col transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>

          {/* Live badge — desktop only */}
          <div className="hidden md:inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-7 w-fit">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            {URGENCY_JOBS_COUNT}+ Jobs Available Right Now
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-gray-900 leading-[1.04] tracking-tight mb-4 md:mb-6">
            Earn Income.<br />
            <span className="text-emerald-500">Start Today.</span>
          </h1>

          {/* Subtext */}
          <p className="text-gray-500 text-sm md:text-base lg:text-lg leading-relaxed max-w-md mb-7 md:mb-9">
            AI-powered matching connects your skills to real income opportunities —
            <strong className="text-gray-800 font-semibold"> no degree required</strong>.
            Get hired within minutes.
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap gap-3 mb-6 md:mb-8 transition-all duration-700 delay-150 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Link
              to="/find-work"
              className="inline-flex items-center gap-2 px-6 md:px-7 py-3 md:py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line" />
              Find Work
            </Link>
            <Link
              to="/hire"
              className="inline-flex items-center gap-2 px-6 md:px-7 py-3 md:py-3.5 bg-white hover:bg-gray-50 text-gray-800 font-bold text-sm rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-briefcase-line" />
              Hire Now
            </Link>
          </div>

          {/* Quick category tags */}
          <div className={`flex flex-wrap gap-2 mb-8 md:mb-10 transition-all duration-700 delay-200 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {QUICK_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                  selectedTag === tag
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : "bg-white border-gray-200 text-gray-500 hover:border-emerald-400 hover:text-emerald-600"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>


        </div>

        {/* ─── RIGHT: Hero image (desktop only) ─── */}
        <div className={`hidden md:flex justify-center items-end relative transition-all duration-1000 delay-100 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[360px] h-[360px] lg:w-[420px] lg:h-[420px] xl:w-[460px] xl:h-[460px] rounded-full bg-emerald-100/60" />
          <img
            src="https://storage.readdy-site.link/project_files/a975f4d2-df36-474b-ab5c-75267cc8e7a8/75f2f10d-e3a5-4c9e-9c10-66a3caa639b1_ChatGPT-Image-Mar-30-2026-09_16_36-PM.png?v=f5edd74b05aa2c72ebce99ad48465108"
            alt="Gig worker"
            className="relative z-10 w-auto h-[440px] lg:h-[500px] xl:h-[540px] object-contain object-bottom -translate-y-10"
          />
          {/* Floating badges */}
          <div className={`absolute top-8 right-2 lg:right-0 bg-white border border-gray-100 rounded-2xl px-4 py-3 min-w-[152px] transition-all duration-700 delay-500 ease-out ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                <i className="ri-briefcase-4-line text-amber-500 text-sm" />
              </div>
              <span className="text-xs text-gray-400 font-medium">Right now</span>
            </div>
            <div className="text-xl font-extrabold text-gray-900">{URGENCY_JOBS_COUNT}+</div>
            <div className="text-xs text-amber-500 font-semibold">Open Jobs</div>
          </div>
          <div className={`absolute bottom-20 left-0 lg:-left-6 bg-emerald-500 rounded-2xl px-4 py-3 min-w-[148px] transition-all duration-700 delay-600 ease-out ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                <i className="ri-timer-flash-line text-white text-sm" />
              </div>
              <span className="text-xs text-emerald-100 font-medium">Avg. Match</span>
            </div>
            <div className="text-xl font-extrabold text-white">{AVG_MATCH_TIME}</div>
            <div className="text-xs text-emerald-100 font-semibold">To get matched</div>
          </div>
          <div className={`absolute top-[68%] -translate-y-1/2 right-0 lg:-right-4 bg-white border border-gray-100 rounded-2xl px-3.5 py-3 transition-all duration-700 delay-700 ease-out ${visible ? "opacity-100" : "opacity-0"}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-emerald-100">
                <img
                  src="https://readdy.ai/api/search-image?query=smiling%20young%20woman%20worker%20headshot%20portrait%20plain%20neutral%20background%20professional&width=32&height=32&seq=av4&orientation=squarish"
                  alt="Worker"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-900 whitespace-nowrap">Maria G. — Hired!</div>
                <div className="text-[10px] text-emerald-500 font-semibold">Cleaning · 2 min ago</div>
              </div>
              <div className="w-5 h-5 flex items-center justify-center bg-emerald-50 rounded-full flex-shrink-0">
                <i className="ri-check-line text-emerald-500 text-xs" />
              </div>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
}
